export interface PerformerClientFilterState {
  company: string;
  associateCompany: string;
  stateName: string;
  locations: string[];
  year: string;
  month: string;
  establishmentType: string;
  employer: string;
  employerLocation: string;
  subContractor: string;
  subContractorLocation: string;
}

export interface PerformerClientSearchConfig {
  searchInput: string;
  activeSort: string;
  sortType: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
}

export const INITIAL_PERFORMER_CLIENT_FILTER_STATE: PerformerClientFilterState = {
  company: '',
  associateCompany: '',
  stateName: '',
  locations: [],
  year: '',
  month: '',
  establishmentType: '',
  employer: '',
  employerLocation: '',
  subContractor: '',
  subContractorLocation: ''
};

export const INITIAL_PERFORMER_CLIENT_SEARCH_CONFIG: PerformerClientSearchConfig = {
  searchInput: '',
  activeSort: 'name',
  sortType: 'asc',
  page: 0,
  rowsPerPage: 10
};

export const PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS = {
  company: {
    only: ['id', 'name']
  },
  associate_company: {
    only: ['id', 'name']
  },
  state: {
    only: ['id', 'name']
  },
  location: {
    only: ['id', 'name']
  },
  state_registers: {
    include: ['registers']
  }
};

export const PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS_CLRA = {
  ...PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS,
  employer: {
    only: ['id', 'name']
  },
  employer_location: {
    only: ['id', 'location_name']
  },
  sub_contractor: {
    only: ['id', 'name']
  },
  sub_contractor_location: {
    only: ['id', 'location_name']
  }
};

export const PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS = [
  'company','associate_company','state','location', 'state_registers'
];

export const PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS_CLRA = [
  'company','associate_company','state','location', 'state_registers',
  'employer','employer_location','sub_contractor','sub_contractor_location'
];

export const PERFORMER_CLIENT_MONTH_LIST = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Alias for backwards compatibility
export const MONTH_LIST = PERFORMER_CLIENT_MONTH_LIST;

export const generatePerformerClientYearsList = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 1950; i--) {
    years.push(i);
  }
  return years;
};

// Alias for backwards compatibility
export const generateYearsList = generatePerformerClientYearsList;

export const formatPerformerClientDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

// Alias for backwards compatibility
export const formatDate = formatPerformerClientDate;

export const getPerformerClientNameById = <T extends { id: string; name: string }>(
  id: string,
  items: T[]
): string => {
  return ((items || []).find(item => item.id === id) || {}).name || '';
};

// Alias for backwards compatibility
export const getNameById = getPerformerClientNameById;

export const buildPerformerClientQueryPayload = (
  filters: PerformerClientFilterState,
  searchConfig: PerformerClientSearchConfig,
  overrides: Partial<any> = {},
  sortField?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  const baseFilters: any = {};

  if (filters.company) baseFilters.company_id_eq = filters.company;
  if (filters.associateCompany) baseFilters.associate_company_id_eq = filters.associateCompany;
  if (filters.year) baseFilters.year_eq = filters.year;
  if (filters.month) baseFilters.month_eq = filters.month;
  if (filters.establishmentType) baseFilters.establishment_type_eq = filters.establishmentType;
  if (filters.stateName) baseFilters.state_id_eq = filters.stateName;

  // Handle location logic for CLRA vs non-CLRA
  if (filters.locations.length > 0) {
    if (filters.establishmentType === 'CLRA') {
      baseFilters.location_id_eq = filters.locations[0];
    } else {
      baseFilters.location_id_in = filters.locations;
    }
  }

  // Add employer filters if present
  if (filters.employer) baseFilters.employer_id_eq = filters.employer;
  if (filters.employerLocation) baseFilters.employer_location_id_eq = filters.employerLocation;
  if (filters.subContractor) baseFilters.sub_contractor_id_eq = filters.subContractor;
  if (filters.subContractorLocation) baseFilters.sub_contractor_location_id_eq = filters.subContractorLocation;

  return {
    filters: { ...baseFilters, ...overrides },
    include: filters.establishmentType === 'CLRA' ? PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS_CLRA : PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS,
    includes: filters.establishmentType === 'CLRA' ? PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS_CLRA : PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS,
    per: searchConfig.rowsPerPage,
    page: searchConfig.page + 1,
    sorts: sortField ? [`${sortField} ${sortDirection || 'asc'}`] : [`created_at desc`]
  };
};

export const getPerformerClientResetFields = (changedField: keyof PerformerClientFilterState): Partial<PerformerClientFilterState> => {
  switch (changedField) {
    case 'establishmentType':
      return {
        company: '',
        associateCompany: '',
        stateName: '',
        locations: [],
        year: '',
        month: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'company':
      return {
        associateCompany: '',
        stateName: '',
        locations: [],
        year: '',
        month: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'associateCompany':
      return {
        locations: [],
        year: '',
        month: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'stateName':
      return {
        locations: [],
        year: '',
        month: ''
      };
    case 'locations':
      return {
        year: '',
        month: '',
        employer: '',
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'year':
      return {
        month: ''
      };
    case 'employer':
      return {
        employerLocation: '',
        subContractor: '',
        subContractorLocation: ''
      };
    case 'employerLocation':
      return {
        subContractor: '',
        subContractorLocation: ''
      };
    case 'subContractor':
      return {
        subContractorLocation: ''
      };
    default:
      return {};
  }
};

// Alias for backwards compatibility
export const getResetFields = getPerformerClientResetFields;

export const getPerformerClientFilterKey = (field: keyof PerformerClientFilterState): string => {
  const keyMap: Record<keyof PerformerClientFilterState, string> = {
    company: 'company_id_eq',
    associateCompany: 'associate_company_id_eq',
    stateName: 'state_id_eq',
    locations: 'location_id_in',
    year: 'year_eq',
    month: 'month_eq',
    establishmentType: 'establishment_type_eq',
    employer: 'employer_id_eq',
    employerLocation: 'employer_location_id_eq',
    subContractor: 'sub_contractor_id_eq',
    subContractorLocation: 'sub_contractor_location_id_eq'
  };

  return keyMap[field];
};

// Sample format S3 keys mapping for configuration types
export const PERFORMER_CLIENT_SAMPLE_FORMAT_S3_KEY_MAPPING: Record<string, any> = {
  'Shops': {
    'Employee': 'Input-Uploads-Templates/Employee/Shops/Employee-Shops.xlsx',
    'Employee Attendance': 'Input-Uploads-Templates/Employee-Attendance/Shops/Employee-Attendance-Shops.xlsx',
    'Leave Credit': 'Input-Uploads-Templates/Leave-Credit/Shops/Leave-Credit-Shops.xlsx',
    'Leave Availed': 'Input-Uploads-Templates/Leave-Availed/Shops/Leave-Availed-Shops.xlsx',
    'Employee Wage': 'Input-Uploads-Templates/Employee-Wage/Shops/Employee-Wage-Shops.xlsx'
  },
  'CLRA': {
    'Employee': 'Input-Uploads-Templates/Employee/CLRA/Employee-CLRA.xlsx',
    'Employee Attendance': 'Input-Uploads-Templates/Employee-Attendance/CLRA/Employee-Attendance-CLRA.xlsx',
    'Leave Credit': 'Input-Uploads-Templates/Leave-Credit/CLRA/Leave-Credit-CLRA.xlsx',
    'Leave Availed': 'Input-Uploads-Templates/Leave-Availed/CLRA/Leave-Availed-CLRA.xlsx',
    'Employee Wage': 'Input-Uploads-Templates/Employee-Wage/CLRA/Employee-Wage-CLRA.xlsx'
  }
};
